import { Controller } from '@hotwired/stimulus'

interface ModalController {
  open: () => void
  close: () => void
}

interface FlatpickrInstance {
  clear: (triggerChange?: boolean) => void
  close: () => void
  setDate: (date: string, triggerChange?: boolean, format?: string) => void
  set: (option: string, value: unknown) => void
  config: { dateFormat: string }
  altInput?: HTMLInputElement
}

interface FlatpickrInputElement extends HTMLInputElement {
  _flatpickrInstance?: FlatpickrInstance
}

export default class extends Controller {
  static targets = ['date', 'comment', 'hidden', 'confirm']
  static values = { modalId: String, today: String, persistedDate: String }

  declare readonly dateTarget: FlatpickrInputElement
  declare readonly commentTarget: HTMLTextAreaElement
  declare readonly hiddenTarget: HTMLInputElement
  declare readonly confirmTarget: HTMLButtonElement
  declare readonly hasConfirmTarget: boolean
  declare readonly modalIdValue: string
  declare readonly todayValue: string
  declare readonly hasTodayValue: boolean
  declare readonly persistedDateValue: string
  declare readonly hasPersistedDateValue: boolean

  private lastAcceptedValue = ''
  private lastAcceptedJustification = ''
  private awaitingJustification = false
  private isRestoring = false

  private readonly handleDialogCloseBound = (): void => { this.handleDialogClose() }

  connect (): void {
    this.lastAcceptedValue = this.dateTarget.value !== '' ? this.dateTarget.value : this.persistedDate()
    this.lastAcceptedJustification = this.hiddenTarget.value
    this.dialog?.addEventListener('close', this.handleDialogCloseBound)
  }

  disconnect (): void {
    this.dialog?.removeEventListener('close', this.handleDialogCloseBound)
  }

  check (): void {
    if (this.isRestoring) return

    const value = this.dateTarget.value

    if (value === '' || !this.isPast(value)) {
      this.lastAcceptedValue = value
      this.resetComment()
      return
    }

    if (this.isAlreadyAcceptedPastDate(value)) {
      this.lastAcceptedValue = value
      return
    }

    this.commentTarget.value = this.lastAcceptedJustification
    this.updateConfirmState()
    this.closeDatepicker()
    this.openModal()
    this.commentTarget.focus()
  }

  confirm (): void {
    if (this.commentTarget.value.trim() === '') return

    this.hiddenTarget.value = this.commentTarget.value.trim()
    this.lastAcceptedValue = this.dateTarget.value
    this.lastAcceptedJustification = this.hiddenTarget.value
    this.commentTarget.value = ''
    this.awaitingJustification = false
    this.closeModal()
    this.scheduleCloseDatepicker()
  }

  cancel (): void {
    this.discardPendingBackdate()
    this.awaitingJustification = false
    this.closeModal()
    this.scheduleCloseDatepicker()
  }

  updateConfirmState (): void {
    if (!this.hasConfirmTarget) return

    this.confirmTarget.disabled = this.commentTarget.value.trim() === ''
  }

  private handleDialogClose (): void {
    if (!this.awaitingJustification) return

    this.discardPendingBackdate()
    this.awaitingJustification = false
    this.scheduleCloseDatepicker()
  }

  private discardPendingBackdate (): void {
    this.restoreDate(this.lastAcceptedValue)
    this.commentTarget.value = ''
    this.hiddenTarget.value = this.lastAcceptedJustification
    this.updateConfirmState()
  }

  private resetComment (): void {
    this.commentTarget.value = ''
    this.hiddenTarget.value = ''
    this.lastAcceptedJustification = ''
    this.updateConfirmState()
  }

  private isAlreadyAcceptedPastDate (value: string): boolean {
    return value === this.persistedDate() || value === this.lastAcceptedValue
  }

  private persistedDate (): string {
    if (!this.hasPersistedDateValue) return ''
    if (!this.isIsoDate(this.persistedDateValue)) return ''

    return this.persistedDateValue
  }

  private restoreDate (value: string): void {
    this.isRestoring = true

    try {
      const flatpickr = this.dateTarget._flatpickrInstance

      if (flatpickr != null) {
        if (value === '') {
          flatpickr.clear(false)
        } else {
          flatpickr.setDate(value, false, flatpickr.config.dateFormat)
        }
      } else {
        this.dateTarget.value = value
      }
    } finally {
      queueMicrotask(() => { this.isRestoring = false })
    }
  }

  private isPast (value: string): boolean {
    if (!this.isIsoDate(value)) return false

    return value < this.today()
  }

  private today (): string {
    if (this.hasTodayValue && this.isIsoDate(this.todayValue)) {
      return this.todayValue
    }

    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  private isIsoDate (value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(value)
  }

  private scheduleCloseDatepicker (): void {
    setTimeout(() => { this.closeDatepickerKeepingInputFocus() }, 0)
  }

  private closeDatepicker (): void {
    this.dateTarget._flatpickrInstance?.close()
  }

  private closeDatepickerKeepingInputFocus (): void {
    const instance = this.dateTarget._flatpickrInstance
    if (instance == null) return

    const visibleInput = instance.altInput ?? this.dateTarget

    instance.set('clickOpens', false)
    instance.close()
    visibleInput.focus({ preventScroll: true })
    visibleInput.setSelectionRange(visibleInput.value.length, visibleInput.value.length)
    instance.set('clickOpens', true)
  }

  private get dialog (): HTMLDialogElement | null {
    const modalElement = document.getElementById(this.modalIdValue)
    if (modalElement == null) return null

    return modalElement.querySelector('dialog')
  }

  private get modalController (): ModalController | null {
    const modalElement = document.getElementById(this.modalIdValue)
    if (modalElement == null) return null

    return this.application.getControllerForElementAndIdentifier(modalElement, 'modal') as ModalController | null
  }

  private openModal (): void {
    this.awaitingJustification = true
    this.modalController?.open()
  }

  private closeModal (): void {
    this.modalController?.close()
  }
}
